/**
 * Merge the curated fitness-first taxonomy with the live "sampled" property
 * and event metadata returned by the existing fetch hooks, then split into:
 *
 *   - `categories`  — curated buckets (Membership, Visits & attendance, …)
 *   - `extras`      — the "All fields" / "All events" escape hatches that
 *                     surface everything sampled but not yet curated.
 *
 * The shape that comes back is what `<AddCriteriaMenu>` renders directly.
 */
import type { AvailableCustomerEvent } from "@/api/customer-event";
import type {
  ConditionKey,
  EventKey,
  Integration,
  IntegrationProviderId,
  PropertyType,
} from "@/api/types";
import { fieldHasInstalledSource, pickActiveSource, resolveVertical } from "./pick-source";
import {
  getVerticalTaxonomy,
  groupTaxonomyByCategory,
  type TaxonomyCategory,
  type TaxonomyField,
  type Vertical,
} from "./segment-taxonomy";

export interface CuratedMenuEntry {
  field: TaxonomyField;
  resolvedKey: string;
  resolvedProvider: IntegrationProviderId;
}

export interface CuratedCategoryGroup {
  category: TaxonomyCategory;
  entries: CuratedMenuEntry[];
}

export interface ExtraPropertyEntry {
  kind: "property";
  label: string;
  value: string;
  source: IntegrationProviderId;
  type: PropertyType;
}

export interface ExtraEventEntry {
  kind: "event";
  label: string;
  value: string;
  source: IntegrationProviderId;
  type: IntegrationProviderId;
}

export interface MergedTaxonomy {
  /** Curated vertical the categories were drawn from. */
  vertical: Vertical;
  /** Curated, fitness-labeled categories. */
  categories: CuratedCategoryGroup[];
  /** Sampled properties not surfaced by the curated taxonomy. */
  extraProperties: ExtraPropertyEntry[];
  /** Sampled events not surfaced by the curated taxonomy. */
  extraEvents: ExtraEventEntry[];
}

interface MergeInput {
  /** Output of `useFetchCustomerPropertiesAsOptions` (flattened). */
  conditionOptions: ConditionKey[];
  /** Output of `useFetchCustomerEventsAsOptions` (flattened). */
  eventOptions: EventKey[];
  /** Raw `available-events` response, used so escape hatches can list events even when no integrations are installed yet. */
  availableEvents?: AvailableCustomerEvent[];
  /** Gym's integrations (active or not). */
  installedIntegrations: Integration[] | undefined;
}

/**
 * Build the menu model rendered by `<AddCriteriaMenu>`.
 *
 * Curated entries only appear when at least one of their declared sources
 * matches an installed+active integration on the gym. Unsupported curated
 * entries fall through to the extras list.
 */
export function mergeTaxonomyWithSampled(input: MergeInput): MergedTaxonomy {
  const sampledPropertyKeys = new Set(input.conditionOptions.map((o) => o.value));
  const sampledEventNames = new Set(input.eventOptions.map((o) => o.value));

  // Pick the curated vertical (fitness vs nightlife) from the gym's installed
  // integrations, then only surface that vertical's categories.
  const vertical = resolveVertical(input.installedIntegrations);
  const { taxonomy, categories: verticalCategories } = getVerticalTaxonomy(vertical);

  const groups = groupTaxonomyByCategory(taxonomy, verticalCategories);
  const categories: CuratedCategoryGroup[] = [];
  for (const { category, fields } of groups) {
    const entries: CuratedMenuEntry[] = [];
    for (const field of fields) {
      if (!fieldHasInstalledSource(field, input.installedIntegrations)) continue;
      const picked = pickActiveSource(field, input.installedIntegrations, {
        sampledPropertyKeys,
        sampledEventNames,
      });
      if (!picked) continue;
      if (field.kind === "property") {
        const key = (picked.source as { key: string }).key;
        entries.push({ field, resolvedKey: key, resolvedProvider: picked.provider });
      } else {
        const event = (picked.source as { event: string }).event;
        entries.push({ field, resolvedKey: event, resolvedProvider: picked.provider });
      }
    }
    if (entries.length > 0) {
      // Events first, then properties alphabetically by label.
      entries.sort((a, b) => {
        const aIsEvent = a.field.kind === "event" ? 0 : 1;
        const bIsEvent = b.field.kind === "event" ? 0 : 1;
        if (aIsEvent !== bIsEvent) return aIsEvent - bIsEvent;
        return a.field.label.localeCompare(b.field.label);
      });
      categories.push({ category, entries });
    }
  }

  // Raw catalogs: surface *everything* sampled (no curated filtering) so the
  // "All fields" / "All events" views feel complete and predictable.
  // Sort alphabetically by source then by label for stable group rendering.
  const extraProperties: ExtraPropertyEntry[] = input.conditionOptions
    .map((option) => ({
      kind: "property" as const,
      label: option.label,
      value: option.value,
      source: option.source,
      type: option.type,
    }))
    .sort((a, b) => a.source.localeCompare(b.source) || a.label.localeCompare(b.label));

  const extraEvents: ExtraEventEntry[] = input.eventOptions
    .map((option) => ({
      kind: "event" as const,
      label: option.label,
      value: option.value,
      source: option.source,
      type: option.type,
    }))
    .sort((a, b) => a.source.localeCompare(b.source) || a.label.localeCompare(b.label));

  return {
    vertical,
    categories,
    extraProperties,
    extraEvents,
  };
}
